import * as THREE from 'three'
import { MeshResourceTracker } from '@/plugins/mesh/MeshResourceTracker'
import type { MeshPrimitive, MeshPrimitiveGroup, MeshRenderClass } from '@/plugins/mesh/types'

/** Primitive 规范化结果。 */
export interface MeshPrimitiveNormalizationResult {
  readonly root: THREE.Group
  readonly primitives: readonly MeshPrimitive[]
  readonly boundingBox: THREE.Box3
  readonly sourcePrimitiveMap: ReadonlyMap<string, readonly MeshPrimitive[]>
  readonly resources: MeshResourceTracker
}

interface PrimitiveMaterialSelection {
  readonly material: THREE.Material
  readonly group?: MeshPrimitiveGroup
}

/** 按材质透明语义确定 Primitive 的渲染分类。 */
export function classifyMeshMaterial(material: THREE.Material): MeshRenderClass {
  if (material.transparent) return 'blend'
  if (material.alphaTest > 0) return 'mask'
  return 'opaque'
}

function selectPrimitiveMaterials(mesh: THREE.Mesh): PrimitiveMaterialSelection[] {
  if (!Array.isArray(mesh.material)) {
    return [{ material: mesh.material }]
  }

  if (mesh.geometry.groups.length === 0) {
    const material = mesh.material[0]
    return material ? [{ material }] : []
  }

  const selections: PrimitiveMaterialSelection[] = []
  mesh.geometry.groups.forEach((group, index) => {
    const materialIndex = group.materialIndex ?? 0
    const material = mesh.material[materialIndex]
    if (!material || group.count <= 0) return
    selections.push({
      material,
      group: {
        index,
        start: group.start,
        count: group.count,
        materialIndex,
      },
    })
  })
  return selections
}

function createGroupGeometry(
  source: THREE.BufferGeometry,
  group: MeshPrimitiveGroup
): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry()
  geometry.name = source.name
  geometry.userData = { ...source.userData }
  geometry.setIndex(source.getIndex())
  for (const [name, attribute] of Object.entries(source.attributes)) {
    geometry.setAttribute(name, attribute)
  }
  for (const [name, attributes] of Object.entries(source.morphAttributes)) {
    geometry.morphAttributes[name] = attributes
  }
  geometry.morphTargetsRelative = source.morphTargetsRelative
  geometry.setDrawRange(group.start, group.count)
  return geometry
}

function computeLocalBoundingBox(
  geometry: THREE.BufferGeometry,
  group?: MeshPrimitiveGroup
): THREE.Box3 {
  const position = geometry.getAttribute('position')
  if (!position) return new THREE.Box3()

  if (!group) {
    if (!geometry.boundingBox) geometry.computeBoundingBox()
    return geometry.boundingBox?.clone() ?? new THREE.Box3()
  }

  const boundingBox = new THREE.Box3()
  const point = new THREE.Vector3()
  const index = geometry.getIndex()
  const end = Math.min(group.start + group.count, index?.count ?? position.count)
  for (let offset = Math.max(0, group.start); offset < end; offset += 1) {
    const vertexIndex = index ? index.getX(offset) : offset
    point.fromBufferAttribute(position, vertexIndex)
    boundingBox.expandByPoint(point)
  }
  return boundingBox
}

function countTriangles(geometry: THREE.BufferGeometry, group?: MeshPrimitiveGroup): number {
  const count =
    group?.count ?? geometry.getIndex()?.count ?? geometry.getAttribute('position')?.count
  return Math.floor((count ?? 0) / 3)
}

function countVertices(geometry: THREE.BufferGeometry, group?: MeshPrimitiveGroup): number {
  if (!group) return geometry.getAttribute('position')?.count ?? 0
  const index = geometry.getIndex()
  if (!index) return group.count

  const vertices = new Set<number>()
  const end = Math.min(group.start + group.count, index.count)
  for (let offset = Math.max(0, group.start); offset < end; offset += 1) {
    vertices.add(index.getX(offset))
  }
  return vertices.size
}

function configurePrimitiveObject(
  source: THREE.Mesh,
  geometry: THREE.BufferGeometry,
  material: THREE.Material,
  worldMatrix: THREE.Matrix4,
  id: string
): THREE.Mesh {
  const object =
    source instanceof THREE.InstancedMesh
      ? new THREE.InstancedMesh(geometry, material, source.count)
      : new THREE.Mesh(geometry, material)
  if (object instanceof THREE.InstancedMesh && source instanceof THREE.InstancedMesh) {
    object.instanceMatrix.copy(source.instanceMatrix)
    object.instanceColor =
      (source.instanceColor?.clone() as THREE.InstancedBufferAttribute | undefined) ?? null
    object.morphTexture = source.morphTexture
    object.computeBoundingBox()
    object.computeBoundingSphere()
  }
  object.name = source.name ? `${source.name}:${id}` : id
  object.matrixAutoUpdate = false
  object.matrix.copy(worldMatrix)
  object.matrixWorld.copy(worldMatrix)
  object.visible = source.visible
  object.castShadow = source.castShadow
  object.receiveShadow = source.receiveShadow
  object.frustumCulled = source.frustumCulled
  object.renderOrder = source.renderOrder
  object.layers.mask = source.layers.mask
  object.userData = { ...source.userData }
  return object
}

function computePrimitiveBoundingBox(
  source: THREE.Mesh,
  geometry: THREE.BufferGeometry,
  group: MeshPrimitiveGroup | undefined,
  worldMatrix: THREE.Matrix4
): THREE.Box3 {
  const localBounds = computeLocalBoundingBox(geometry, group)
  if (!(source instanceof THREE.InstancedMesh)) {
    return localBounds.applyMatrix4(worldMatrix)
  }

  const bounds = new THREE.Box3()
  const instanceMatrix = new THREE.Matrix4()
  const combinedMatrix = new THREE.Matrix4()
  for (let index = 0; index < source.count; index += 1) {
    source.getMatrixAt(index, instanceMatrix)
    combinedMatrix.multiplyMatrices(worldMatrix, instanceMatrix)
    bounds.union(localBounds.clone().applyMatrix4(combinedMatrix))
  }
  return bounds
}

/** 将静态 Object3D 层级展开为按单一材质可独立路由的 Primitive。 */
export class MeshPrimitiveNormalizer {
  normalize(
    sourceRoot: THREE.Object3D,
    resources = new MeshResourceTracker()
  ): MeshPrimitiveNormalizationResult {
    sourceRoot.updateWorldMatrix(true, true)
    resources.trackObject3D(sourceRoot)

    const root = new THREE.Group()
    root.name = sourceRoot.name || 'mesh'
    root.matrixAutoUpdate = false
    const primitives: MeshPrimitive[] = []
    const boundingBox = new THREE.Box3()
    const sourcePrimitiveMap = new Map<string, MeshPrimitive[]>()

    sourceRoot.traverse((object) => {
      if (!(object instanceof THREE.Mesh) || object instanceof THREE.SkinnedMesh) return
      this.appendMeshPrimitives(
        object,
        root,
        primitives,
        boundingBox,
        sourcePrimitiveMap,
        resources
      )
    })

    return {
      root,
      primitives,
      boundingBox,
      sourcePrimitiveMap,
      resources,
    }
  }

  private appendMeshPrimitives(
    source: THREE.Mesh,
    root: THREE.Group,
    primitives: MeshPrimitive[],
    assetBoundingBox: THREE.Box3,
    sourcePrimitiveMap: Map<string, MeshPrimitive[]>,
    resources: MeshResourceTracker
  ): void {
    const selections = selectPrimitiveMaterials(source)
    selections.forEach((selection, selectionIndex) => {
      const geometry = selection.group
        ? resources.trackGeometry(createGroupGeometry(source.geometry, selection.group))
        : source.geometry
      const worldMatrix = source.matrixWorld.clone()
      const id = `${source.uuid}:${selection.group?.index ?? selectionIndex}`
      const object = configurePrimitiveObject(source, geometry, selection.material, worldMatrix, id)
      const boundingBox = computePrimitiveBoundingBox(
        source,
        source.geometry,
        selection.group,
        worldMatrix
      )
      if (object instanceof THREE.InstancedMesh && object.morphTexture) {
        resources.trackTexture(object.morphTexture)
      }
      const instanceCount = source instanceof THREE.InstancedMesh ? source.count : 1
      const primitive: MeshPrimitive = {
        id,
        sourceNodeId: source.uuid,
        sourceNodeName: source.name,
        object,
        geometry,
        material: selection.material,
        worldMatrix,
        boundingBox,
        renderClass: classifyMeshMaterial(selection.material),
        group: selection.group,
        triangleCount: countTriangles(source.geometry, selection.group) * instanceCount,
        vertexCount: countVertices(source.geometry, selection.group) * instanceCount,
      }

      root.add(object)
      primitives.push(primitive)
      assetBoundingBox.union(boundingBox)
      const mapped = sourcePrimitiveMap.get(source.uuid) ?? []
      mapped.push(primitive)
      sourcePrimitiveMap.set(source.uuid, mapped)
    })
  }
}
